import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Stack } from '@ltvco/refresh-lib/theme';
import { DateUtil, isZeroed } from '@ltvco/refresh-lib/utils';
import { DebugMenu, ReportLoading, ReportFactory } from '@ltvco/refresh-lib/v1';
import {
  ReportNullState,
  FraudUrlOverview as FraudUrlOverviewV2,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  ReportActionsWithDateUpdated,
  ReportRating,
  AdditionalUrlDetailsSection as AdditionalDetailsSection,
  RiskDetailsSection,
} from '@ltvco/refresh-lib/v2';

import { ReportRouteProps } from 'components/reports/types';
import { getFraudUrlNavLinkDataV2 } from 'navLinkData/fraudNavLinkData';

export function UrlFraudReportPage({ permalink }: ReportRouteProps) {
  // hooks
  const queryResult = useReport(permalink);
  const zeroed = isZeroed(queryResult);

  // constants
  const reportType = 'url-fraud';
  const urlSearchParams = new URLSearchParams(window.location.search);

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  const report = ReportFactory.create(queryResult?.data, 'url-fraud');
  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  if (!report) {
    return <ReportLoading menuItems={2} />;
  }
  if (report?.data?.isEmpty) {
    return <ReportNullState />;
  }

  let url = report.data.url || urlSearchParams.get('url') || '';
  if (url.toLowerCase().startsWith('www.')) {
    url = url.replace('www.', 'https://');
  }
  if (!url.toLowerCase().startsWith('http')) {
    url = `https://${url}`;
  }
  const { hostname } = new URL(url);
  document.title = `${hostname} - BeenVerified`;
  const fraudUrlNavLinkDataV2 = getFraudUrlNavLinkDataV2();

  const {
    domain,
    isSuspicious,
    isUnsafe,
    isAdult,
    isHostedContent,
    isPhishing,
    isSpam,
    countryCode,
    pageTitle,
    pageSize,
    category,
    isRedirected,
    isMalware,
    statusCode,
    isShortLinkRedirect,
    technologies,
    contentType,
    server,
    riskScore,
    display,
    extended,
  } = report.data;

  const {
    domain: domainName,
    isRiskyTld,
    age,
    isParked,
    trust,
    isDmarcRecord,
    isSpfRecord,
    domainRank,
    mxRecords,
    nsRecords,
  } = domain;

  return (
    <Box>
      <Stack direction="row">
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Fraud Scan"
            headerTitle={url}
            menuItems={
              fraudUrlNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        <Box marginTop={0}>
          <Box
            sx={(theme) => ({
              height: 30,
              backgroundColor: theme.palette.background.default,
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                md: 'block',
              },
            })}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Url Fraud"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
          />

          <FraudUrlOverviewV2
            domain={domainName}
            isSuspicious={isSuspicious}
            isUnsafe={isUnsafe}
            domainTrust={trust}
            isMalware={isMalware}
            isPhishing={isPhishing}
            pageTitle={pageTitle}
            siteCategory={category}
            riskScore={riskScore || 0}
            countryCode={countryCode}
          />

          <Stack spacing={2}>
            <RiskDetailsSection
              isRiskyTld={isRiskyTld}
              isAdult={isAdult}
              isParked={isParked}
              statusCode={statusCode}
              isSpam={isSpam}
              isShortLinkRedirect={isShortLinkRedirect}
            />
            <AdditionalDetailsSection
              isRedirected={isRedirected}
              finalUrl={display || extended || url}
              isDmarcRecord={isDmarcRecord}
              hostedContent={isHostedContent}
              domainAge={age}
              pageSize={pageSize}
              domainRank={domainRank}
              nsRecords={nsRecords}
              technologies={technologies}
              spfRecord={isSpfRecord}
              contentType={contentType}
              server={server}
              mxRecords={mxRecords}
            />
            <ReportRating
              rating={report.data.meta?.rating || null}
              report_type={reportType}
            />
          </Stack>
        </Box>
      </Stack>
      <ReportNavigationMobileMenu
        reportType="Fraud Scan"
        headerTitle={url}
        menuItems={
          fraudUrlNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
        }
      />
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </Box>
  );
}
